import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Ban,
  CheckCircle2,
  KeyRound,
  Trash2,
  UserCog,
  Shield,
  Package,
  ClipboardList,
  DollarSign,
} from "lucide-react";

const PLANS = ["free", "pro", "enterprise"];

const StatBox = ({ icon: Icon, label, value, tone = "amber" }) => (
  <div className="surface-card rounded-xl p-5 relative overflow-hidden">
    <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-30 ${
      tone === "red" ? "bg-red-500" : tone === "blue" ? "bg-blue-500" : "bg-amber-500"
    }`} />
    <div className="flex items-center justify-between mb-3 relative">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex">
        {label}
      </div>
      <Icon className={`w-4 h-4 ${
        tone === "red" ? "text-red-400" : tone === "blue" ? "text-blue-400" : "text-amber-500"
      }`} />
    </div>
    <div className="font-display text-3xl font-bold">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tempPasswordModal, setTempPasswordModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/admin/customers"),
        api.get("/admin/stats"),
      ]);
      setCustomers(c.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const changePlan = async (u, plan) => {
    try {
      await api.put(`/admin/customers/${u.id}/plan`, { plan });
      toast.success(`${u.email} → ${plan}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSuspend = async (u) => {
    try {
      await api.put(`/admin/customers/${u.id}/suspend`, { suspended: !u.suspended });
      toast.success(u.suspended ? "Reactivated" : "Suspended");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (u) => {
    if (!confirm(`Reset password for ${u.email}? A new temporary password will be generated.`)) return;
    try {
      const r = await api.post(`/admin/customers/${u.id}/reset-password`);
      setTempPasswordModal(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (u) => {
    if (!confirm(`DELETE ${u.email} and all their data (inventory, POs, sales)? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/customers/${u.id}`);
      toast.success("Customer deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const impersonate = async (u) => {
    if (!confirm(`Log in as ${u.email}? You'll see the app exactly as they do. Use "Return to admin" banner to come back.`)) return;
    try {
      await api.post(`/admin/customers/${u.id}/impersonate`);
      toast.success(`Now viewing as ${u.email}`);
      if (refresh) await refresh();
      navigate("/");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (user?.role !== "superadmin") {
    return (
      <div className="text-slate-400" data-testid="admin-forbidden">
        You don't have access to this page.
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-root">
      <div>
        <div className="text-xs uppercase tracking-widest text-red-400 font-mono-plex mb-2 flex items-center gap-2">
          <Shield className="w-3 h-3" /> Operator Console
        </div>
        <h1 className="font-display text-4xl font-bold">Customers</h1>
        <p className="text-slate-500 text-sm mt-1">
          Manage all bars using Barkeep. Change plans, suspend, reset passwords, or impersonate to debug.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatBox icon={Users} label="Customers" value={stats.total_users} />
          <StatBox icon={Ban} label="Suspended" value={stats.suspended_users} tone="red" />
          <StatBox icon={Package} label="Total SKUs" value={stats.total_items} tone="blue" />
          <StatBox icon={ClipboardList} label="Total POs" value={stats.total_purchase_orders} />
        </div>
      )}

      <div className="surface-card rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-slate-500 text-sm">Loading customers…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-slate-500 text-xs uppercase tracking-widest">
                <th className="text-left px-6 py-3">Customer</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">SKUs</th>
                <th className="text-right px-4 py-3">POs</th>
                <th className="text-right px-4 py-3">Sales</th>
                <th className="text-left px-4 py-3">Signed up</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${c.id}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium flex items-center gap-2">
                      {c.name}
                      {c.role === "superadmin" && (
                        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded font-mono-plex">
                          you
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-mono-plex">{c.email}</div>
                    <div className="text-xs text-slate-500">{c.bar_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={c.plan}
                      onChange={(e) => changePlan(c, e.target.value)}
                      disabled={c.role === "superadmin"}
                      data-testid={`plan-select-${c.id}`}
                      className="bg-[#0a0c10] border border-white/10 rounded-md px-2 py-1 text-xs outline-none focus:border-amber-500 capitalize disabled:opacity-50"
                    >
                      {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {c.suspended ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-red-500/10 text-red-400 border-red-500/20 font-mono-plex">
                        <Ban className="w-3 h-3" /> suspended
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-mono-plex">
                        <CheckCircle2 className="w-3 h-3" /> active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-plex text-slate-400">{c.items_count}</td>
                  <td className="px-4 py-3 text-right font-mono-plex text-slate-400">{c.pos_count}</td>
                  <td className="px-4 py-3 text-right font-mono-plex text-slate-400">{c.sales_count}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs font-mono-plex">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex gap-1 justify-end">
                      <ActionBtn
                        title="Impersonate"
                        onClick={() => impersonate(c)}
                        disabled={c.role === "superadmin" || c.suspended}
                        testId={`impersonate-${c.id}`}
                      >
                        <UserCog className="w-3.5 h-3.5" />
                      </ActionBtn>
                      <ActionBtn
                        title="Reset password"
                        onClick={() => resetPassword(c)}
                        testId={`reset-${c.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                      </ActionBtn>
                      <ActionBtn
                        title={c.suspended ? "Reactivate" : "Suspend"}
                        onClick={() => toggleSuspend(c)}
                        disabled={c.role === "superadmin"}
                        tone={c.suspended ? "green" : "red"}
                        testId={`suspend-${c.id}`}
                      >
                        <Ban className="w-3.5 h-3.5" />
                      </ActionBtn>
                      <ActionBtn
                        title="Delete"
                        onClick={() => del(c)}
                        disabled={c.role === "superadmin"}
                        tone="red"
                        testId={`delete-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {tempPasswordModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-[#12141a] border border-white/10 rounded-xl p-6 space-y-4">
            <div className="font-display text-2xl font-bold">Temporary password generated</div>
            <p className="text-sm text-slate-400">
              Share this with <span className="text-white font-mono-plex">{tempPasswordModal.email}</span> so they can log in. They should change it right after.
            </p>
            <div className="bg-[#0a0c10] border border-amber-500/40 rounded-md px-4 py-3 font-mono-plex text-amber-400 text-center text-lg break-all">
              {tempPasswordModal.temporary_password}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(tempPasswordModal.temporary_password);
                  toast.success("Copied");
                }}
                className="px-4 py-2 rounded-md border border-white/10 text-sm hover:bg-white/[0.03]"
              >
                Copy
              </button>
              <button
                onClick={() => setTempPasswordModal(null)}
                className="btn-amber rounded-md px-4 py-2 text-sm"
                data-testid="close-temp-password"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ActionBtn = ({ children, title, tone = "default", disabled, onClick, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
      tone === "red"
        ? "hover:bg-red-500/10 text-red-400"
        : tone === "green"
        ? "hover:bg-emerald-500/10 text-emerald-400"
        : "hover:bg-white/[0.05] text-slate-400"
    }`}
  >
    {children}
  </button>
);
